import { useEffect, useMemo, useState } from "react";
import { fetchHistorialData } from "./useHistorialApi";

const PAGE_SIZE = 20;

const SORTABLE_COLUMNS = {
    adversario: "adversario",
    pais: "pais",
    jugados: "jugados",
    ganados: "ganados",
    perdidos: "perdidos",
    diff: "diff",
    winRate: "winRate",
};

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeSearchText = (value = "") =>
    value
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLocaleLowerCase("es");

const normalizeTeam = (row = {}) => {
    const jugados = toNumber(row.jugados);
    const ganados = toNumber(row.ganados);
    const perdidos = toNumber(row.perdidos);
    const winRate = jugados > 0 ? (ganados / jugados) * 100 : 0;

    return {
        adversario: row.adversario || "-",
        pais: row.pais || null,
        jugados,
        ganados,
        perdidos,
        diff: ganados - perdidos,
        winRate,
    };
};

function sortRows(rows, sortBy, sortDirection) {
    const sortFactor = sortDirection === "asc" ? 1 : -1;

    return [...rows].sort((left, right) => {
        if (sortBy === "adversario" || sortBy === "pais") {
            const leftStr = left[sortBy] ?? "";
            const rightStr = right[sortBy] ?? "";
            return leftStr.localeCompare(rightStr, "es", { sensitivity: "base" }) * sortFactor;
        }

        const leftValue = toNumber(left[sortBy]);
        const rightValue = toNumber(right[sortBy]);

        if (leftValue === rightValue) {
            return left.adversario.localeCompare(right.adversario, "es", { sensitivity: "base" });
        }

        return (leftValue - rightValue) * sortFactor;
    });
}

function useHistorialData() {
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [historial, setHistorial] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState("jugados");
    const [sortDirection, setSortDirection] = useState("desc");
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        let mounted = true;

        const load = async () => {
            setIsLoading(true);
            setIsError(false);

            try {
                const data = await fetchHistorialData();

                if (!mounted) {
                    return;
                }

                const normalized = Array.isArray(data)
                    ? data.map((row) => normalizeTeam(row))
                    : [];

                setHistorial(normalized);
            } catch (error) {
                if (mounted) {
                    setIsError(true);
                }
            } finally {
                if (mounted) {
                    setIsLoading(false);
                }
            }
        };

        load();

        return () => {
            mounted = false;
        };
    }, []);

    const filteredHistorial = useMemo(() => {
        const normalizedSearch = normalizeSearchText(searchTerm.trim());

        if (!normalizedSearch) {
            return historial;
        }

        return historial.filter((row) => {
            const searchableText = `${row.adversario} ${row.pais || ""}`;
            return normalizeSearchText(searchableText).includes(normalizedSearch);
        });
    }, [historial, searchTerm]);

    const sortedHistorial = useMemo(
        () => sortRows(filteredHistorial, sortBy, sortDirection),
        [filteredHistorial, sortBy, sortDirection]
    );

    const totalItems = sortedHistorial.length;

    const totalPages = useMemo(
        () => Math.max(1, Math.ceil(totalItems / PAGE_SIZE)),
        [totalItems]
    );

    const safePage = Math.min(currentPage, totalPages);

    const pagedHistorial = useMemo(() => {
        const start = (safePage - 1) * PAGE_SIZE;
        return sortedHistorial.slice(start, start + PAGE_SIZE);
    }, [safePage, sortedHistorial]);

    const range = useMemo(() => {
        if (!totalItems) {
            return { start: 0, end: 0 };
        }

        const start = (safePage - 1) * PAGE_SIZE + 1;
        const end = Math.min(safePage * PAGE_SIZE, totalItems);
        return { start, end };
    }, [safePage, totalItems]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const handleSortChange = (column) => {
        if (!SORTABLE_COLUMNS[column]) {
            return;
        }

        if (sortBy === column) {
            setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
            return;
        }

        setSortBy(column);
        setSortDirection("desc");
        setCurrentPage(1);
    };

    return {
        isLoading,
        isError,
        currentPage: safePage,
        setCurrentPage,
        totalPages,
        totalItems,
        totalAvailable: historial.length,
        searchTerm,
        setSearchTerm,
        sortBy,
        sortDirection,
        onSortChange: handleSortChange,
        pagedHistorial,
        range,
    };
}

export default useHistorialData;
